// Which keycaps the customizer's clicks select, out of CustomizerScreen (backfill PR 25):
// the first real keycap (the tour's keycap, and the Shift range's anchor while nothing is
// selected), whether a keycap holds an entry (the tour adds one when not), and the
// selection after a click on a keycap in the colour, text or icon editor: a plain click,
// a Cmd/Ctrl toggle or a Shift range from the last selected keycap, with the ISO Enter flag,
// and what the click writes to the design (PR 31).
export function findFirstActualKeycap(keycapData) {
  for (let rowIndex = 0; rowIndex < keycapData.length; rowIndex++) {
    const row = keycapData[rowIndex];
    for (let keycapIndex = 0; keycapIndex < row.length; keycapIndex++) {
      const keycap = row[keycapIndex];
      if (!keycap.isSpacer) {
        return { rowIndex, keycapIndex };
      }
    }
  }

  return null; // or throw an error if one must exist
} // this function returns the rowIndex and keycapIndex of the first non-spacer keycap inside the designs keycapData

export function hasKeycapEntries(
  keycapData,
  rowIndex,
  keycapIndex,
  entriesKey
) {
  const keycap = keycapData?.[rowIndex]?.[keycapIndex];
  if (!keycap || typeof keycap[entriesKey] !== 'object') return false;

  return Object.keys(keycap[entriesKey]).length > 0;
} // whether the keycap at the position has at least one entry under entriesKey ('texts' or 'icons'); hasKeycapTexts and hasKeycapIcons, identical apart from the key, until PR 25

function buildRange(anchor, target, keycapData) {
  const selectedRange = [];

  const forward =
    anchor.rowIndex < target.rowIndex ||
    (anchor.rowIndex === target.rowIndex &&
      anchor.keycapIndex <= target.keycapIndex); // whether the anchor comes before the target in row/keycap order: the range runs from the anchor toward the target, so a target before the anchor is walked backwards
  const step = forward ? 1 : -1;

  for (
    let rowIndex = anchor.rowIndex;
    forward ? rowIndex <= target.rowIndex : rowIndex >= target.rowIndex;
    rowIndex += step
  ) {
    // go through every row from the anchor's to the target's:
    const lastIndex = keycapData[rowIndex].length - 1;
    let startKeycapIndex = forward ? 0 : lastIndex; // the whole row, for the rows between the anchor's and the target's
    let endKeycapIndex = forward ? lastIndex : 0;
    if (rowIndex === anchor.rowIndex) {
      startKeycapIndex = anchor.keycapIndex; // the anchor's row starts at the anchor
    }
    if (rowIndex === target.rowIndex) {
      endKeycapIndex = target.keycapIndex; // the target's row ends at the target (both, when they share a row)
    }

    for (
      let keycapIndex = startKeycapIndex;
      forward ? keycapIndex <= endKeycapIndex : keycapIndex >= endKeycapIndex;
      keycapIndex += step
    ) {
      if (!keycapData[rowIndex][keycapIndex].isSpacer) {
        selectedRange.push({ rowIndex, keycapIndex }); // a keycap of the range (the spacers are left out)
      }
    }
  }

  return selectedRange; // ordered from anchor toward target, spacers excluded
} // builds the continuous range between the anchor and the target (inclusive) for multiselecting keycaps, skipping the spacers. The anchor and the target are keycaps of the rows (a layout switch resets the selection, a loaded design brings its own, and the layout validator refuses empty rows), so PR 25 dropped the clamping of a stale anchor and the empty-row skips, and folded the forward and backward loops into one

function uniqKeycaps(arr) {
  const seen = new Set();
  const out = [];

  for (const item of arr) {
    const key = `${item.rowIndex}:${item.keycapIndex}`;

    if (!seen.has(key)) {
      // if the current key (keycap) has not been seen yet:
      seen.add(key); // add it to the seen set
      out.push(item); // push it to the out array
    }
  }

  return out;
} // this helper function makes sure there are no duplicates in the selectedKeycaps

export function nextKeycapSelection(
  keycapData,
  selectedKeycaps,
  clicked,
  { shift: isShift, multiToggle: isMultiToggle }
) {
  const { rowIndex, keycapIndex } = clicked;

  const isAlreadySelected = selectedKeycaps.some(
    (keycap) =>
      keycap.rowIndex === rowIndex && keycap.keycapIndex === keycapIndex
  ); // check if the clicked keycap is already part of the selected keycaps

  const lastSelectedKeycap =
    selectedKeycaps[selectedKeycaps.length - 1] || null; // Determine the range anchor (last selected keycap)

  const anchor = lastSelectedKeycap || findFirstActualKeycap(keycapData); // the range's anchor: the last selected keycap, else the first keycap of the layout (a click needs a keycap, so there is one)

  let newSelectedKeycaps;

  if (isShift && !isMultiToggle) {
    // Shift ONLY (add contiguous range to existing selection):
    const range = buildRange(anchor, clicked, keycapData); // build the continuous range
    newSelectedKeycaps = uniqKeycaps([...selectedKeycaps, ...range]); // make sure there are no duplicates after adding the already selectedKeycaps and the range together
  } else if (isMultiToggle) {
    // Cmd/Ctrl (with or without Shift): toggle single clicked item
    newSelectedKeycaps = isAlreadySelected
      ? selectedKeycaps.filter(
          (keycap) =>
            !(
              keycap.rowIndex === rowIndex && keycap.keycapIndex === keycapIndex
            )
        )
      : [...selectedKeycaps, clicked];
  } else {
    // No modifiers: single-select semantics
    const isOnlyOneSelected = selectedKeycaps.length === 1 && isAlreadySelected;

    newSelectedKeycaps = isOnlyOneSelected
      ? [] // Deselect if it was the only one selected and clicked again
      : [clicked]; // Otherwise, reset to this one only
  }

  // check if any of the selected keycaps are ISO Enter:
  const isIsoEnterSelected = newSelectedKeycaps.some(
    ({ rowIndex, keycapIndex }) => {
      const keycap = keycapData?.[rowIndex]?.[keycapIndex]; // the `?.` are for the tour's fallback selection before the layout has loaded, which points nowhere (KNOWN_BUGS.md); every other selected position is a keycap of the rows
      return keycap?.isIsoEnter;
    }
  );

  return {
    selectedKeycaps: newSelectedKeycaps,
    isoEnterSelected: isIsoEnterSelected,
  };
} // the selection after the click and whether it holds the ISO Enter keycap, as keycapClickUpdates writes them to the design's settings

export function keycapClickUpdates(currentDesign, clicked, event) {
  const editor = currentDesign.settings.selectedEditor;

  if (editor === 'colors' || editor === 'text' || editor === 'icons') {
    const keycapData = currentDesign.keycapData;
    const selectedKeycaps = currentDesign.settings.selectedKeycaps;

    const isMultiToggle = event?.metaKey || event?.ctrlKey; // check if the user currently has Ctrl (Windows) or Cmd (macOS) pressed
    const isShift = !!event?.shiftKey; // check if the user has shift currently pressed while selecting keycaps

    const selection = nextKeycapSelection(
      keycapData,
      selectedKeycaps,
      clicked,
      { shift: isShift, multiToggle: isMultiToggle }
    ); // the selection after this click (plain, Cmd/Ctrl toggle or Shift range) and whether it holds the ISO Enter

    return {
      'settings.selectedKeycaps': selection.selectedKeycaps,
      'settings.isoEnterSelected': selection.isoEnterSelected,
      'keycapColorEditor.selectedColorEntry': null, // reset the selectedColorEntry
      'textEditor.selectedColorEntry': null, // reset the selectedColorEntry
      'textEditor.selectedTextIndex': null, // reset text entry selection
      'textEditor.selectedTag': null, // reset tag selection
      'iconEditor.selectedColorEntry': null, // reset the selectedColorEntry
      'iconEditor.selectedIconIndex': null, // reset icon entry selection
      'iconEditor.selectedTag': null, // reset tag selection
    };
  }

  return null;
} // what a click on a keycap writes to the design: in the colour, text and icon editors the new selection with every editor's entry, tag and colour selection reset, in the other editors nothing (the layout and profile editors don't select keycaps; backfill PR 31, out of the screen's handleKeycapClick and enterKeycapEditingMode)
